/**
 * React hook для управления шаблонами упражнений
 */

import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import apiService from '../services/api';
import { ExerciseTemplate, ExerciseTemplateCreate, ExerciseTemplateUpdate } from '../types';
import { useAuth } from './useAuth';

export const useTemplates = () => {
  const queryClient = useQueryClient();
  const { trainer } = useAuth();

  // Запрос списка шаблонов
  const {
    data: templates = [],
    isLoading,
    error,
    refetch
  } = useQuery(
    ['templates', trainer?.id],
    () => apiService.getExerciseTemplates(),
    {
      enabled: !!trainer?.id,
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке шаблонов';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для создания шаблона
  const createTemplateMutation = useMutation(
    (data: ExerciseTemplateCreate) => apiService.createExerciseTemplate(data),
    {
      onSuccess: () => {
        toast.success('Шаблон создан успешно!');
        queryClient.invalidateQueries('templates');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при создании шаблона';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для обновления шаблона
  const updateTemplateMutation = useMutation(
    ({ id, data }: { id: number; data: ExerciseTemplateUpdate }) => 
      apiService.updateExerciseTemplate(id, data),
    {
      onSuccess: () => {
        toast.success('Шаблон обновлен успешно!');
        queryClient.invalidateQueries('templates');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при обновлении шаблона';
        toast.error(errorMessage);
      }
    }
  );

  // Мутация для удаления шаблона
  const deleteTemplateMutation = useMutation(
    (id: number) => apiService.deleteExerciseTemplate(id),
    {
      onSuccess: () => {
        toast.success('Шаблон удален успешно!');
        queryClient.invalidateQueries('templates');
      },
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при удалении шаблона';
        toast.error(errorMessage);
      }
    }
  );

  return {
    // Данные
    templates,
    isLoading,
    error,
    
    // Действия
    createTemplate: createTemplateMutation.mutate,
    updateTemplate: updateTemplateMutation.mutate,
    deleteTemplate: deleteTemplateMutation.mutate,
    refetch,
    
    // Состояния мутаций
    isCreateLoading: createTemplateMutation.isLoading,
    isUpdateLoading: updateTemplateMutation.isLoading,
    isDeleteLoading: deleteTemplateMutation.isLoading,
  };
};

// Хук для работы с одним шаблоном
export const useTemplate = (id: number) => {
  const queryClient = useQueryClient();

  const {
    data: template,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['template', id],
    () => apiService.getExerciseTemplate(id),
    {
      enabled: !!id,
      onError: (error: any) => {
        const errorMessage = error.detail || 'Ошибка при загрузке шаблона';
        toast.error(errorMessage);
      }
    }
  );

  return {
    template,
    isLoading,
    error,
    refetch,
  };
}; 